import React, { useEffect, useRef, useState } from "react";

function VisaWorld() {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRefs = useRef([]);
  const contentRef = useRef(null);

  useEffect(() => {
    sectionRefs.current = sectionRefs.current.slice(0, 5);

    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = sectionRefs.current.indexOf(entry.target);
          setActiveIndex(index);
        }
      });
    }, observerOptions);

    sectionRefs.current.forEach((section) => {
      observer.observe(section);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const scrollToSection = (index) => {
    setActiveIndex(index);
    const section = sectionRefs.current[index];
    const contentElement = contentRef.current;

    const scrollPosition = section.offsetTop - contentElement.offsetTop;

    contentElement.scrollTo({
      top: scrollPosition,
      behavior: "smooth",
    });
  };

  return (
    <div className="flex flex-wrap">
      {/* Left-side Navigation */}
      <div className="sticky top-0 h-screen w-1/4 bg-gray-100 p-4 hidden md:block">
        <ul className="space-y-4">
          <li
            onClick={() => scrollToSection(0)}
            className={`cursor-pointer p-4 rounded-md shadow-md ${
              activeIndex === 0
                ? "bg-blue-500 text-white"
                : "bg-white text-[#2C3F69]"
            }`}
          >
            Types of Degrees
          </li>
          <li
            onClick={() => scrollToSection(1)}
            className={`cursor-pointer p-4 rounded-md shadow-md ${
              activeIndex === 1
                ? "bg-blue-500 text-white"
                : "bg-white text-[#2C3F69]"
            }`}
          >
            Admission Requirements
          </li>
          <li
            onClick={() => scrollToSection(2)}
            className={`cursor-pointer p-4 rounded-md shadow-md ${
              activeIndex === 2
                ? "bg-blue-500 text-white"
                : "bg-white text-[#2C3F69]"
            }`}
          >
            Student Visa Requirements
          </li>
          <li
            onClick={() => scrollToSection(3)}
            className={`cursor-pointer p-4 rounded-md shadow-md ${
              activeIndex === 3
                ? "bg-blue-500 text-white"
                : "bg-white text-[#2C3F69]"
            }`}
          >
            Tuition Fees
          </li>
          <li
            onClick={() => scrollToSection(4)}
            className={`cursor-pointer p-4 rounded-md shadow-md ${
              activeIndex === 4
                ? "bg-blue-500 text-white"
                : "bg-white text-[#2C3F69]"
            }`}
          >
            Scholarships & Why Study in Denmark
          </li>
        </ul>
      </div>

      {/* Content Section */}
      <div
        ref={contentRef}
        className="mb-10 h-screen overflow-y-auto p-4 w-full md:w-3/4"
      >
        <div ref={(el) => (sectionRefs.current[0] = el)}>
          <p className="text-xl font-bold text-[#011473]">Types of Degrees</p>
          <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
            <li>
              <strong>Academy Profession Degree:</strong> A pre-Bachelor's program lasting 1.5 to 2.5 years with mandatory work placements and an option to extend to a Bachelor's degree with 1.5 more years of study.
            </li>
            <li>
              <strong>Professional Bachelor's Degree:</strong> A 3- to 4-year vocational degree with in-service training and eligibility for a Master's degree.
            </li>
            <li>
              <strong>General Bachelor's Degree:</strong> Standard and arts-focused degrees lasting 3 years (or 4 for certain arts programs) and leading to a Master’s degree.
            </li>
            <li>
              <strong>Diploma Degree:</strong> A 2-year preparatory course for specialized fields, requiring prior qualifications and work experience.
            </li>
            <li>
              <strong>Master’s Programs:</strong> General, arts and continuing education Master’s programs lasting 2 to 3 years, tailored to professional and academic fields.
            </li>
            <li>
              <strong>PhD Programs:</strong> Research-based programs requiring 8 years of higher education, completed in 3 years of independent research.
            </li>
          </ul>
        </div>

        <div ref={(el) => (sectionRefs.current[1] = el)} className="mt-10">
          <p className="text-lg font-semibold text-[#011473]">Admission Requirements</p>
          <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
            <li>
              <strong>Undergraduate:</strong> Submit certified educational certificates and meet Danish standards or pass entrance exams if needed.
            </li>
            <li>
              <strong>Postgraduate:</strong> A Bachelor’s degree with 180 ECTS credits is essential, with some universities offering integrated PhD programs.
            </li>
            <li>
              <strong>Language Proficiency:</strong> Proof of English proficiency through IELTS, TOEFL, or Cambridge ESOL. For Danish programs, pass exams like Studieprøven or Danskprøve 2.
            </li>
          </ul>
        </div>

        <div ref={(el) => (sectionRefs.current[2] = el)} className="mt-10">
          <p className="text-lg font-semibold text-[#011473]">Student Visa Requirements</p>
          <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
            <li>Acceptance letter from a Danish university.</li>
            <li>Proof of language proficiency in English or Danish.</li>
            <li>Evidence of financial support (€1,000/month).</li>
            <li>Proof of travel insurance and accommodation.</li>
            <li>A valid passport, recent photos and visa fee payment receipt.</li>
          </ul>
        </div>

        <div ref={(el) => (sectionRefs.current[3] = el)} className="mt-10">
          <p className="text-lg font-semibold text-[#011473]">Tuition Fees</p>
          <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
            <li>International students pay between €6,000 and €16,000 annually.</li>
            <li>Specialized programs like medicine may cost up to €35,000 annually.</li>
            <li>Tuition waivers apply for permanent residents or students staying with a parent holding such a permit.</li>
          </ul>
        </div>

        <div ref={(el) => (sectionRefs.current[4] = el)} className="mt-10">
          <p className="text-lg font-semibold text-[#011473]">Scholarships & Why Study in Denmark</p>
          <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
            <li>
              <strong>Scholarships:</strong> Funding programs like Nordplus, Erasmus and international scholarships for Bachelor's, Master's and PhD students.
            </li>
            <li>
              <strong>Why Study in Denmark:</strong> Modern, innovative society with high living standards, affordable tuition, globally recognized education and a welcoming environment.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default VisaWorld;
